<template>
  <div class="admin-layout" :class="{ 'sidebar-open': sidebarOpen }">
    <!-- Overlay pour fermer la sidebar en mobile -->
    <div class="sidebar-overlay" @click="closeSidebar"></div>
    
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'sidebar-collapsed': sidebarCollapsed, 'sidebar-mobile-open': sidebarOpen }">
      <!-- Sidebar Header -->
      <div class="sidebar-header">
        <div class="logo-container">
          <div class="logo-icon">
            <img src="@/assets/amana1.jpeg" alt="Inveep GPAO" class="logo-image" />
          </div>
          <div class="logo-text" v-if="!sidebarCollapsed">
            <h2>Inveep GPAO</h2>
            <span class="logo-subtitle">Management Suite</span>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <nav class="sidebar-nav">
        <div class="nav-section">
          <div class="nav-section-title" v-if="!sidebarCollapsed">
            <span>Navigation</span>
            <div class="section-line"></div>
          </div>
          <router-link
            v-for="item in menuItems"
            :key="item.name"
            :to="item.path"
            class="nav-item"
            :class="{ active: $route.name === item.name }"
            :title="sidebarCollapsed ? item.title : ''"
            @click="closeSidebar"
          >
            <div class="nav-icon">
              <i :class="item.icon"></i>
            </div>
            <span class="nav-text" v-if="!sidebarCollapsed">{{ item.title }}</span>
            <div class="nav-indicator" v-if="$route.name === item.name"></div>
            <div class="nav-ripple"></div>
          </router-link>
        </div>

      </nav>

      <!-- Sidebar Footer -->
      <div class="sidebar-footer">
        <div class="user-card" v-if="user">
          <div class="user-avatar-container">
            <div class="user-avatar">
              <img v-if="user.avatar" :src="user.avatar" :alt="user.prenom" />
              <i v-else class="fas fa-user"></i>
            </div>
            <div class="user-status-indicator"></div>
          </div>
          <div class="user-info" v-if="!sidebarCollapsed">
            <div class="user-name">{{ user.prenom }} {{ user.nom }}</div>
            <div class="user-role">{{ user.role }}</div>
            <div class="user-status">En ligne</div>
          </div>
          <button 
            class="user-menu-btn" 
            v-if="!sidebarCollapsed"
            @click="toggleUserMenu"
          >
            <i class="fas fa-ellipsis-v"></i>
          </button>
        </div>

        <!-- Version Info -->
        <div class="version-info" v-if="!sidebarCollapsed">
          <span>Version 1.0.0</span>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Header -->
      <header class="header">
        <div class="header-left">
          <!-- Sidebar Toggle -->
          <button 
            class="sidebar-toggle-header"
            @click="toggleSidebar"
            :title="sidebarCollapsed ? 'Étendre le menu' : 'Réduire le menu'"
          >
            <i class="fas fa-bars"></i>
          </button>
          
          <h1 class="page-title">{{ currentPageTitle }}</h1>
        </div>
        
        <div class="header-right">
          <!-- User Menu -->
          <div class="user-menu" @click="toggleUserMenu">
            <div class="user-avatar">
              <i class="fas fa-user"></i>
            </div>
            <span class="user-name">{{ user?.prenom }}</span>
            <i class="fas fa-chevron-down"></i>
            
            <!-- Dropdown -->
            <div class="user-dropdown" v-if="showUserMenu">
              <router-link to="/profile" class="dropdown-item">
                <i class="fas fa-user"></i>
                Mon Profil
              </router-link>
              <div class="dropdown-divider"></div>
              <button @click="logout" class="dropdown-item logout-btn">
                <i class="fas fa-sign-out-alt"></i>
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="page-content">
        <transition name="fade" mode="out-in">
          <router-view />
        </transition>
      </main>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'

export default {
  name: 'Layout',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const toast = useToast()

    const sidebarCollapsed = ref(false)
    const sidebarOpen = ref(false) // Pour mobile
    const showUserMenu = ref(false)

    const user = computed(() => store.getters['auth/user'])
    const isAdmin = computed(() => store.getters['auth/isAdmin'])
    const userRole = computed(() => store.getters['auth/userRole'])

    const menuItems = computed(() => {
      const items = []

      // Menu spécial pour le SuperAdmin
      if (userRole.value === 'SuperAdmin') {
        return [
          {
            name: 'SuperAdmin',
            path: '/superadmin',
            title: 'SaaS SuperAdmin',
            icon: 'fas fa-server'
          },
          {
            name: 'Profile',
            path: '/profile',
            title: 'Mon Profil',
            icon: 'fas fa-user-circle'
          }
        ]
      }

      // Dashboard - Masqué pour les Commerciaux, Techniciens, Financiers et Magasiniers
      if (!['Commercial', 'Technicien', 'Financier', 'Magasinier'].includes(userRole.value)) {
        items.push({
          name: 'Dashboard',
          path: '/dashboard',
          title: 'Dashboard',
          icon: 'fas fa-chart-pie'
        })
      }

      // Gestion Stock - Visible uniquement pour Admin et Magasinier
      if (['Admin', 'Magasinier'].includes(userRole.value)) {
        items.push({
          name: 'Stock',
          path: '/stock',
          title: 'Gestion Stock',
          icon: 'fas fa-warehouse'
        })
        items.push({
          name: 'Boms',
          path: '/boms',
          title: 'Nomenclatures',
          icon: 'fas fa-sitemap'
        })
        items.push({
          name: 'ProductionOrders',
          path: '/production',
          title: 'Ordres de Fab.',
          icon: 'fas fa-industry'
        })
        items.push({
          name: 'Workflows',
          path: '/workflows',
          title: 'Workflows & Centres',
          icon: 'fas fa-project-diagram'
        })
        items.push({
          name: 'QualityPlans',
          path: '/quality',
          title: 'Contrôle Qualité',
          icon: 'fas fa-clipboard-check'
        })
        items.push({
          name: 'ManufacturingAI',
          path: '/manufacturing-ai',
          title: 'Optimisation IA',
          icon: 'fas fa-brain'
        })
        items.push({
          name: 'ComputerVision',
          path: '/computer-vision',
          title: 'Vision Industrielle',
          icon: 'fas fa-eye'
        })
      }

      // Planning & RDV - Masqué pour les Financiers et Magasiniers
      if (!['Financier', 'Magasinier'].includes(userRole.value)) {
        items.push({
          name: 'Planning',
          path: '/planning',
          title: 'Planning & RDV',
          icon: 'fas fa-calendar-check'
        })
      }

      // Clients - Visible pour Admin et Commercial
      if (['Admin', 'Commercial', 'Responsable Planning'].includes(userRole.value)) {
        items.push({
          name: 'Clients',
          path: '/clients',
          title: 'Clients',
          icon: 'fas fa-users'
        })
      }

      // Interventions - Visible pour Technicien, Admin, Planning (masqué pour Magasinier)
      if (['Technicien', 'Admin', 'Responsable Planning'].includes(userRole.value)) {
        items.push({
          name: 'Operations',
          path: '/interventions',
          title: 'Operations',
          icon: 'fas fa-tools'
        })
      }

      // Caissier - Pour Techniciens et Financiers
      if (['Technicien', 'Financier', 'Admin'].includes(userRole.value)) {
        items.push({
          name: 'Caissier',
          path: '/caissier',
          title: 'Espace Financier',
          icon: 'fas fa-cash-register'
        })
      }

      // Espace Achat - Visible pour Admin, Magasinier, Financier
      if (['Admin', 'Magasinier', 'Financier'].includes(userRole.value)) {
        items.push({
          name: 'Achats',
          path: '/achats',
          title: 'Espace Achat',
          icon: 'fas fa-shopping-cart'
        })
      }

      // Utilisateurs - Seulement pour les Admins
      if (isAdmin.value) {
        items.push({
          name: 'Users',
          path: '/users',
          title: 'Utilisateurs',
          icon: 'fas fa-users-cog'
        })
      }

      // Administration (Catégories) - Seulement pour les Admins
      if (isAdmin.value) {
        items.push({
          name: 'Admin',
          path: '/admin',
          title: 'Administration',
          icon: 'fas fa-cog'
        })
      }

      // Mon Profil - Toujours visible
      items.push({
        name: 'Profile',
        path: '/profile',
        title: 'Mon Profil',
        icon: 'fas fa-user-circle'
      })

      return items
    })

    const currentPageTitle = computed(() => {
      const currentRoute = menuItems.value.find(item => item.name === route.name)
      // Titre par défaut selon le rôle
      let defaultTitle = 'Dashboard'
      if (userRole.value === 'Commercial') {
        defaultTitle = 'Interventions'
      } else if (userRole.value === 'Technicien') {
        defaultTitle = 'Planning & RDV'
      } else if (userRole.value === 'Financier') {
        defaultTitle = 'Espace Financier'
      } else if (userRole.value === 'Magasinier') {
        defaultTitle = 'Gestion Stock'
      }
      return currentRoute?.title || defaultTitle
    })

    const toggleSidebar = () => {
      // En mode mobile, on toggle l'ouverture de la sidebar
      if (window.innerWidth <= 1024) {
        sidebarOpen.value = !sidebarOpen.value
      } else {
        // En mode desktop, on toggle le collapse
        sidebarCollapsed.value = !sidebarCollapsed.value
      }
    }

    const closeSidebar = () => {
      sidebarOpen.value = false
    }

    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value
    }

    const closeUserMenu = (event) => {
      if (!event.target.closest('.user-menu')) {
        showUserMenu.value = false
      }
    }

    const logout = async () => {
      try {
        store.dispatch('auth/logout')
        toast.success('Déconnexion réussie')
        router.push('/login')
      } catch (error) {
        toast.error('Erreur lors de la déconnexion')
      }
    }

    // Redirection automatique pour les Commerciaux, Techniciens, Financiers et Magasiniers
    const handleRoleRedirect = () => {
      if (userRole.value === 'Commercial' && (route.path === '/dashboard' || route.path === '/')) {
        router.push('/stock')
      } else if (userRole.value === 'Technicien' && (route.path === '/dashboard' || route.path === '/stock' || route.path === '/')) {
        router.push('/planning')
      } else if (userRole.value === 'Financier' && (route.path === '/dashboard' || route.path === '/planning' || route.path === '/')) {
        router.push('/caissier')
      } else if (userRole.value === 'Magasinier' && (route.path === '/dashboard' || route.path === '/')) {
        router.push('/stock')
      }
    }

    onMounted(() => {
      document.addEventListener('click', closeUserMenu)
      // Vérifier la redirection au montage
      handleRoleRedirect()
    })

    // Surveiller les changements de route pour rediriger si nécessaire
    watch([userRole, route], () => {
      handleRoleRedirect()
    })

    onUnmounted(() => {
      document.removeEventListener('click', closeUserMenu)
    })

    return {
      sidebarCollapsed,
      sidebarOpen,
      showUserMenu,
      user,
      isAdmin,
      userRole,
      menuItems,
      currentPageTitle,
      toggleSidebar,
      closeSidebar,
      toggleUserMenu,
      logout
    }
  }
}
</script>

<style scoped>
.admin-layout {
  display: flex;
  height: 100vh;
  background-color: var(--light-gray);
}

/* Sidebar */
.sidebar {
  width: 300px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%);
  color: #1f2937;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  box-shadow: 
    0 10px 25px rgba(0, 0, 0, 0.08),
    0 4px 10px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  position: relative;
  overflow: hidden;
  border-right: 1px solid rgba(226, 232, 240, 0.8);
}

.sidebar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-blue), var(--secondary-blue), var(--accent-orange));
  z-index: 1;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.sidebar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 20%, rgba(16, 185, 129, 0.02) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(255, 128, 64, 0.02) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.sidebar-collapsed {
  width: 80px;
}

/* Sidebar Header */
.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  display: flex;
  align-items: center;
  position: relative;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%);
  backdrop-filter: blur(10px);
  z-index: 2;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.logo-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--accent-orange), #ff9960);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 8px 20px rgba(255, 128, 64, 0.3);
  position: relative;
  overflow: hidden;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.logo-icon::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transform: rotate(45deg);
  animation: shimmer 3s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
  50% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  100% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
}

.logo-text {
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.logo-text h2 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.logo-subtitle {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}


/* Navigation */
.sidebar-nav {
  flex: 1;
  padding: 1rem 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sidebar-nav::-webkit-scrollbar {
  display: none;
}

.nav-section {
  margin-bottom: 2rem;
}

.nav-section-title {
  padding: 0 1.5rem 0.75rem;
  margin-bottom: 0.5rem;
}

.nav-section-title span {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-line {
  height: 2px;
  background: linear-gradient(90deg, var(--accent-orange), rgba(255, 128, 64, 0.3), transparent);
  margin-top: 0.5rem;
  border-radius: 2px;
  box-shadow: 0 1px 3px rgba(255, 128, 64, 0.3);
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1.5rem;
  color: #6b7280;
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  margin: 0.25rem 0.75rem;
  border-radius: 14px;
  font-weight: 500;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  width: calc(100% - 1.5rem);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(226, 232, 240, 0.6);
}

.nav-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 14px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: -1;
  opacity: 0.9;
}

.nav-item:hover {
  color: #1f2937;
  transform: translateX(6px) scale(1.02);
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(0, 27, 183, 0.2);
  box-shadow: 
    0 8px 25px rgba(0, 27, 183, 0.1),
    0 3px 10px rgba(0, 0, 0, 0.05);
}

.nav-item:hover::before {
  width: 4px;
  left: -1px;
  border-radius: 0 14px 14px 0;
}

.nav-item.active {
  color: var(--primary-blue);
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1), rgba(0, 70, 255, 0.05));
  border-color: rgba(0, 27, 183, 0.3);
  box-shadow: 
    0 8px 25px rgba(0, 27, 183, 0.15),
    0 3px 10px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  font-weight: 600;
}

.nav-item.active::before {
  width: 4px;
  left: -1px;
  border-radius: 0 14px 14px 0;
}

.nav-item.active .nav-indicator {
  position: absolute;
  right: 1rem;
  width: 6px;
  height: 6px;
  background: var(--accent-orange);
  border-radius: 50%;
  box-shadow: 0 0 10px var(--accent-orange);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { 
    opacity: 1;
    transform: scale(1);
  }
  50% { 
    opacity: 0.7;
    transform: scale(1.2);
  }
}

.nav-ripple {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 12px;
  overflow: hidden;
  pointer-events: none;
}

.nav-ripple::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.3s ease;
}

.nav-item:active .nav-ripple::before {
  width: 300px;
  height: 300px;
}

.nav-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
}

.nav-text {
  font-size: 0.875rem;
  font-weight: 500;
  transition: opacity 0.1s ease, transform 0.1s ease;
}


/* Sidebar Footer */
.sidebar-footer {
  padding: 1.5rem;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.9) 100%);
  backdrop-filter: blur(15px);
}

.user-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  backdrop-filter: blur(10px);
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 
    0 4px 15px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.user-card:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.1),
    0 4px 10px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 1);
  border-color: rgba(0, 27, 183, 0.2);
}

.user-avatar-container {
  position: relative;
}

.user-avatar {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, var(--accent-orange), #ff9960);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  box-shadow: 0 4px 12px rgba(255, 128, 64, 0.3);
  overflow: hidden;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.user-status-indicator {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  background: #10b981;
  border: 2px solid #1a1d29;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
}

.user-info {
  flex: 1;
  min-width: 0;
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.user-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1f2937;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.125rem;
  font-weight: 500;
}

.user-status {
  font-size: 0.7rem;
  color: #10b981;
  font-weight: 600;
}

.user-menu-btn {
  width: 32px;
  height: 32px;
  background: rgba(0, 27, 183, 0.1);
  border: 1px solid rgba(0, 27, 183, 0.2);
  color: #6b7280;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.user-menu-btn:hover {
  background: rgba(0, 27, 183, 0.15);
  border-color: rgba(0, 27, 183, 0.3);
  color: var(--primary-blue);
  transform: scale(1.05);
}

.version-info {
  text-align: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(226, 232, 240, 0.5);
}

.version-info span {
  font-size: 0.7rem;
  color: #9ca3af;
  font-weight: 500;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.header {
  background-color: var(--white);
  padding: 1rem 2rem;
  border-bottom: 1px solid var(--light-gray);
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--dark-gray);
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

/* Sidebar Toggle in Header */
.sidebar-toggle-header {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1));
  border: 1px solid rgba(16, 185, 129, 0.2);
  color: #374151;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
  font-size: 1.125rem;
}

.sidebar-toggle-header:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15));
  border-color: rgba(16, 185, 129, 0.3);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  color: var(--primary-blue);
}

.sidebar-toggle-header:active {
  transform: scale(0.95);
}

.user-menu {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-menu:hover {
  background-color: var(--light-gray);
}

.user-menu .user-avatar {
  width: 35px;
  height: 35px;
  font-size: 1rem;
}

.user-menu .user-name {
  font-weight: 500;
  color: var(--dark-gray);
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--white);
  border-radius: 0.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 100;
  overflow: hidden;
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: var(--dark-gray);
  text-decoration: none;
  transition: background-color 0.2s ease;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: var(--light-gray);
}

.dropdown-divider {
  height: 1px;
  background-color: var(--light-gray);
  margin: 0.5rem 0;
}

.logout-btn {
  color: var(--error);
}

.logout-btn:hover {
  background-color: rgba(239, 68, 68, 0.1);
}

/* Page Content */
.page-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  background-color: var(--light-gray);
}

/* Sidebar Overlay */
.sidebar-overlay {
  display: none;
}

/* Responsive */
@media (max-width: 1200px) {
  .sidebar {
    width: 280px;
  }
}

@media (max-width: 1024px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1001;
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 280px;
  }

  .sidebar.sidebar-mobile-open {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0;
    width: 100%;
  }

  .sidebar-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }

  .admin-layout.sidebar-open .sidebar-overlay {
    opacity: 1;
    visibility: visible;
  }
}

@media (max-width: 768px) {
  .sidebar {
    width: 280px;
  }

  .sidebar-header {
    padding: 1.5rem 1rem;
  }

  .logo-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .logo-text h2 {
    font-size: 1.25rem;
  }

  .nav-item {
    padding: 0.75rem 1rem;
    margin: 0.25rem 0.5rem;
    width: calc(100% - 1rem);
  }

  .user-card {
    padding: 0.75rem;
  }

  .user-avatar {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }

  .header {
    padding: 1rem;
  }

  .page-content {
    padding: 1rem;
  }
}
</style>
